import { Box } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import Lottie from 'lottie-react'
import { OrganizationAPI } from '../../../../../../../api/organization.api'
import LoaderMain from '../../../../../../../assets/svgIcons/loaderMain.json'
import GlobalContext from '../../../../../../../context/global-context'
import { Title } from '../../../../../ui'
import CustomButton from '../../../../../ui/CustomButton'
import BasicModal from '../../../../../ui/BasicModal'

const InputField = ({ label, placeholder, type = 'text', value, onChange }) => (
  <div>
    <label className='block text-sm font-sans font-medium text-gray-700'>{label}</label>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className='mt-1 block w-full rounded-full border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-sans'
    />
  </div>
)

const normalizeRut = value => String(value || '').replace(/[^0-9kK]/g, '').toUpperCase()

const formatRutInput = input => {
  let cleaned = normalizeRut(input)

  if (cleaned.length > 9) cleaned = cleaned.slice(0, 9)

  const body = cleaned.slice(0, -1)
  const dv = cleaned.slice(-1)
  let formattedBody = ''

  for (let i = body.length - 1, j = 1; i >= 0; i--, j++) {
    formattedBody = body[i] + formattedBody
    if (j % 3 === 0 && i !== 0) {
      formattedBody = '.' + formattedBody
    }
  }

  if (body.length === 0) return dv

  return `${formattedBody}-${dv}`
}

const formatMonto = value => {
  if (!value) return ''

  const numericValue = String(value).replace(/\D/g, '')
  if (!numericValue) return ''

  return new Intl.NumberFormat('es-CL').format(Number(numericValue))
}

const toInputDate = value => {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  return date.toISOString().split('T')[0]
}

const buildInitialFormState = ({ data, folio }) => ({
  razonSocial: data?.razonSocial || '',
  rut: data?.rut ? formatRutInput(data.rut) : '',
  fecha: toInputDate(data?.fecha),
  tipoDocumento: data?.nombreCodigoDocumento || data?.nombreComprobante || '',
  folio: data?.numeroFolio || data?.folio || folio || '',
  monto: data?.montoTotal != null ? String(Math.abs(Number(data.montoTotal))) : ''
})

const DatosReferenciaAclarar = ({
  open,
  setOpen,
  type = 'egreso',
  setSwitchDate,
  idLibro,
  fVenc,
  folio
}) => {
  const { currentBusiness } = useContext(GlobalContext)
  const organizationAPI = new OrganizationAPI()

  const [data, setData] = useState({})
  const [razonesSociales, setRazonesSociales] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState(buildInitialFormState({ data: null, folio }))

  const handleClose = () => setOpen(false)

  useEffect(() => {
    if (!open || !idLibro || !currentBusiness?.id) return

    let active = true

    const loadData = async () => {
      try {
        setLoading(true)

        const [responseData, responseRazones] = await Promise.all([
          organizationAPI.getDataLibroContable(currentBusiness.id, idLibro, type.trim()),
          organizationAPI.getRazonSocial(currentBusiness.id)
        ])

        if (!active) return

        const libroData = responseData?.data || {}

        setData(libroData)
        setFormData(buildInitialFormState({ data: libroData, folio }))
        setRazonesSociales(Array.isArray(responseRazones?.data) ? responseRazones.data : [])
      } catch (error) {
        console.error(error)
      } finally {
        if (active) setLoading(false)
      }
    }

    loadData()

    return () => {
      active = false
    }
  }, [open, idLibro, currentBusiness?.id, type, folio])

  const updateField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async () => {
    if (
      !formData.razonSocial ||
      !formData.rut ||
      !formData.fecha ||
      !formData.tipoDocumento ||
      !formData.monto
    ) {
      alert('Todos los campos son obligatorios excepto folio')
      return
    }

    try {
      setSaving(true)

      const payload = {
        fecha: formData.fecha,
        vencimiento: data?.fechaVencimiento || fVenc || null,
        glosa: data?.glosa || null,
        monto: Number(String(formData.monto).replace(/\D/g, '')),
        razonSocial: formData.razonSocial,
        rut: formData.rut,
        nombreComprobante: formData.tipoDocumento,
        extra: {
          ...(data?.extra && typeof data.extra === 'object' ? data.extra : {}),
          folio: formData.folio || null
        }
      }

      const promises = [
        organizationAPI.updateVinculacion(currentBusiness.id, idLibro, payload)
      ]

      const razonSeleccionada = razonesSociales.find(
        item => normalizeRut(item?.rut) === normalizeRut(formData.rut)
      )

      if (razonSeleccionada?.code) {
        const idRazonSocial = String(razonSeleccionada.code).split('-')[0]
        promises.push(
          organizationAPI.patchRazonSocial(currentBusiness.id, idRazonSocial, {
            razonSocial: formData.razonSocial
          })
        )
      }

      await Promise.all(promises)

      setSwitchDate(true)
      handleClose()
    } catch (error) {
      console.error(error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <BasicModal
      open={open}
      onClose={handleClose}
      width={620}
      height={500}
      minWidth={620}
      minHeight={500}
      footer={
        <>
          <CustomButton
            title='Cerrar'
            color='primary'
            className='rounded-full'
            onClick={handleClose}
            disabled={saving}
          />
          <CustomButton
            title={saving ? 'Guardando...' : 'Guardar'}
            color='accent2'
            className='rounded-full'
            onClick={handleSubmit}
            disabled={saving}
          />
        </>
      }
    >
      {loading ? (
        <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
          <Lottie
            animationData={LoaderMain}
            loop
            autoplay
            style={{ width: 100, height: 100 }}
          />
        </Box>
      ) : (
        <div className='mt-4'>
          <div className='flex justify-between items-center mb-6'>
            <Title className='text-3xl font-sans'>Datos referencia</Title>
          </div>

          <div className='flex w-full items-start h-full p-6 bg-white rounded-lg shadow-md'>
            <div className='flex flex-col gap-6 w-full'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 w-full'>
                <InputField
                  label='Razon Social'
                  placeholder='razon social'
                  value={formData.razonSocial}
                  onChange={e => updateField('razonSocial', e.target.value)}
                />

                <InputField
                  label='RUT'
                  placeholder='11.111.111-1'
                  value={formData.rut}
                  onChange={e => updateField('rut', formatRutInput(e.target.value))}
                />

                <div>
                  <label className='block text-sm font-sans font-medium text-gray-700'>Fecha</label>
                  <input
                    type='date'
                    min='1900-01-01'
                    max='9999-12-31'
                    value={formData.fecha}
                    onChange={e => updateField('fecha', e.target.value)}
                    className='mt-1 block w-full rounded-full border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-sans'
                  />
                </div>

                <InputField
                  label='Tipo Documento'
                  placeholder='tipo documento'
                  value={formData.tipoDocumento}
                  onChange={e => updateField('tipoDocumento', e.target.value)}
                />

                <InputField
                  label='Folio'
                  placeholder='Ingrese folio'
                  value={formData.folio}
                  onChange={e => updateField('folio', e.target.value)}
                />

                <InputField
                  label='Monto'
                  placeholder='0'
                  value={formatMonto(formData.monto)}
                  onChange={e => updateField('monto', e.target.value.replace(/\D/g, ''))}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </BasicModal>
  )
}

export default DatosReferenciaAclarar
